import CardActions from '@material-ui/core/CardActions';
import PendingIcon from '@material-ui/icons/BookmarkBorder';
import ReviewedIcon from '@material-ui/icons/CheckCircle';
import CommentIcon from '@material-ui/icons/Comment';
import EventIcon from '@material-ui/icons/Event';
import EventAvailableIcon from '@material-ui/icons/EventAvailable';
import PhoneIcon from '@material-ui/icons/Phone';
import DateTimeInput from 'ra-datetime-input';
import React from 'react';
import {
  BooleanInput,
  Create,
  Datagrid,
  DisabledInput,
  Edit,
  EditButton,
  Filter,
  FormDataConsumer,
  FunctionField,
  List,
  ListButton,
  LongTextInput,
  NumberField,
  NumberInput,
  ReferenceField,
  ReferenceInput,
  Responsive,
  SaveButton,
  SelectInput,
  SimpleForm,
  SimpleList,
  TextField,
  TextInput,
  Toolbar,
} from 'react-admin';
import DuplicateButton from './duplicateButton';

const moment = require('moment-timezone');

const EventFilter = props => (
  <Filter {...props}>
    <ReferenceInput label="Market" source="market_id" reference="markets">
      <SelectInput optionText="name" />
    </ReferenceInput>
    <SelectInput
      label="Status"
      source="admin_status"
      choices={[
        { _id: '', name: '' },
        { _id: 'pending', name: 'Pending' },
        { _id: 'reviewed', name: 'Reviewed' },
        { _id: 'called', name: 'Called' },
        { _id: 'online_end', name: 'Online End' },
        { _id: 'online_sched', name: 'Online Sched' },
      ]}
      optionText="name"
      optionValue="_id"
    />
    <NumberInput label="Min Capacity" source="capacity" />
    <DateTimeInput label="Start Date" source="start_date" />
    <DateTimeInput label="End Date" source="end_date" />
    <TextInput label="Event Name" source="name" />
    <TextInput label="Venue Name" source="venue_name" />
    <TextInput label="Venue Address" source="venue_address" />
    <BooleanInput label="Show Hiddens" source="hidden" />
    <BooleanInput label="Show Duplicates" source="duplicate_of" />
  </Filter>
);

export const EventList = props => (
  <List {...props} bulkActionButtons={false} filters={<EventFilter />}>
    <Responsive
      small={
        <SimpleList
          primaryText={record => `${record.venue_name}`}
          secondaryText={record => record.name}
          tertiaryText={record =>
            new Date(record.start_date).toLocaleDateString()
          }
        />
      }
      medium={
        <Datagrid>
          <EditButton />
          <FunctionField
            label="Status"
            source="admin_status"
            render={record => {
              switch (record.admin_status) {
                case 'pending':
                  return <PendingIcon />;
                case 'reviewed':
                  return <ReviewedIcon />;
                case 'called':
                  return <PhoneIcon />;
                case 'online_end':
                  return <EventIcon />;
                case 'online_sched':
                  return <EventAvailableIcon />;
                default:
                  return '';
              }
            }}
          />
          <FunctionField
            label=""
            render={record => {
              if (record.notes) {
                return <CommentIcon />;
              } else {
                return '';
              }
            }}
          />
          <TextField source="id" />

          <ReferenceField label="Market" source="market_id" reference="markets">
            <TextField source="short_name" />
          </ReferenceField>
          <TextField source="name" />

          <FunctionField
            source="start_date"
            render={record => {
              var options = {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                timeZone: record.time_zone,
              };

              return new Date(record.start_date).toLocaleDateString(
                'en-us',
                options
              );
            }}
          />

          <FunctionField
            source="end_date"
            render={record => {
              var options = {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                timeZone: record.time_zone,
              };

              var d =
                record.manual_end_date ||
                record.end_date ||
                record.auto_end_date;
              if (d) {
                return new Date(d).toLocaleDateString('en-us', options);
              }
            }}
          />

          <NumberField source="capacity" />
          <FunctionField
            label="Venue"
            render={record => {
              record.venue_address = record.venue_address || '';
              return (
                <div>
                  <div>{record.venue_name}</div>
                  <div>{record.venue_address.split(',')[0]}</div>
                </div>
              );
            }}
          />
        </Datagrid>
      }
    />
  </List>
);

const EventTitle = ({ record }) => {
  return <span>{record ? `"${record.name}"` : ''}</span>;
};

const cardActionStyle = {
  zIndex: 2,
  display: 'inline-block',
  float: 'right',
};

const EventActions = ({ basePath, data }) => (
  <CardActions style={cardActionStyle}>
    <DuplicateButton record={data} />
    <ListButton basePath={basePath} />
  </CardActions>
);

const getOffset = (date, zone) => {
  if (!zone) {
    return 0;
  }

  // get the local utc offset *at the time of the event*
  const localOffset = moment(date).utcOffset();
  // and the event offset *at the time of the event*
  const eventOffset = moment(date)
    .tz(zone)
    .utcOffset();

  return eventOffset - localOffset;
};

// from display to data
const parseDate = timeZone => date => {
  return date == null || timeZone == null
    ? date
    : moment(date)
        .tz(timeZone)
        .subtract(getOffset(date, timeZone), 'minutes')
        .toDate();
};

// from data to display
const formatDate = timeZone => date => {
  return date == null || timeZone == null
    ? date
    : moment(date)
        .tz(timeZone)
        .add(getOffset(date, timeZone), 'minutes')
        .toDate();
};

const WithProps = ({ children, ...props }) => children(props);

const clientTimezone = () => moment.tz.guess(true);

const PostEditToolbar = props => (
  <Toolbar {...props}>
    <SaveButton />
  </Toolbar>
);

export const EventEdit = props => (
  <Edit title={<EventTitle />} actions={<EventActions />} {...props}>
    <WithProps>
      {({ record, ...rest }) => (
        <SimpleForm toolbar={<PostEditToolbar />} record={record} {...rest}>
          <DisabledInput label="Id" source="id" />
          <SelectInput
            label="Status"
            source="admin_status"
            choices={[
              { _id: '', name: '' },
              { _id: 'pending', name: 'Pending' },
              { _id: 'reviewed', name: 'Reviewed' },
              { _id: 'called', name: 'Called' },
              { _id: 'online_end', name: 'Online End' },
              { _id: 'online_sched', name: 'Online Sched' },
            ]}
            optionText="name"
            optionValue="_id"
          />
          <BooleanInput source="hidden" />
          <NumberInput source="duplicate_of" />
          <ReferenceInput
            label="Market"
            source="market_id"
            reference="markets"
            allowEmpty
          >
            <SelectInput optionText="name" />
          </ReferenceInput>
          <LongTextInput source="name" />
          <DateTimeInput
            source="start_date"
            options={{ ampm: true }}
            parse={parseDate(record.time_zone)}
            format={formatDate(record.time_zone)}
          />
          <DateTimeInput
            source="manual_end_date"
            options={{ ampm: true }}
            parse={parseDate(record.time_zone)}
            format={formatDate(record.time_zone)}
          />
          <DisabledInput
            source="end_date"
            parse={parseDate(record.time_zone)}
            format={formatDate(record.time_zone)}
          />
          <DisabledInput
            source="auto_end_date"
            parse={parseDate(record.time_zone)}
            format={formatDate(record.time_zone)}
          />
          <NumberInput source="capacity" />
          <LongTextInput source="venue_name" />
          <LongTextInput source="venue_address" />
          <NumberInput source="latitude" />
          <NumberInput source="longitude" />
          <SelectInput
            label="Source"
            source="source"
            choices={[
              { _id: 'admin', name: 'Admin' },
              { _id: 'user', name: 'User' },
              { _id: 'facebook', name: 'Facebook' },
              { _id: 'seatgeek', name: 'Seatgeek' },
              { _id: 'eventful', name: 'Eventful' },
              { _id: 'ticketmaster', name: 'Ticketmaster' },
            ]}
            optionText="name"
            optionValue="_id"
          />
          <ReferenceInput
            label="Category"
            source="category"
            reference="categories"
            allowEmpty
          >
            <SelectInput optionText="name" optionValue="id" />
          </ReferenceInput>
          <LongTextInput source="notes" />
          {hasAlertPrivileges() && (
            <FormDataConsumer>
              {() => {
                return (
                  <div>
                    <BooleanInput
                      label="Enable Upcoming Alert"
                      source="upcoming_alert_enabled"
                    />
                    <NumberInput
                      label="Upcoming Alert Threshold (mins)"
                      source="upcoming_threshold"
                    />
                    <BooleanInput
                      label="Enable Ending Alert"
                      source="ending_alert_enabled"
                    />
                    <NumberInput
                      label="Ending Alert Threshold (mins)"
                      source="ending_threshold"
                    />
                  </div>
                );
              }}
            </FormDataConsumer>
          )}
        </SimpleForm>
      )}
    </WithProps>
  </Edit>
);

export const EventCreate = props => (
  <Create {...props}>
    <WithProps>
      {({ record, ...rest }) => (
        <SimpleForm record={record} {...rest}>
          <ReferenceInput
            label="Market"
            source="market_id"
            reference="markets"
            allowEmpty
          >
            <SelectInput optionText="name" />
          </ReferenceInput>
          <LongTextInput source="name" />
          <DateTimeInput source="start_date" options={{ ampm: true }} />
          <DateTimeInput source="manual_end_date" options={{ ampm: true }} />
          <NumberInput source="capacity" />
          <LongTextInput source="venue_name" />
          <LongTextInput source="venue_address" />
          <NumberInput source="latitude" />
          <NumberInput source="longitude" />
          <SelectInput
            label="Source"
            source="source"
            choices={[
              { _id: 'admin', name: 'Admin' },
              { _id: 'user', name: 'User' },
              { _id: 'facebook', name: 'Facebook' },
              { _id: 'seatgeek', name: 'Seatgeek' },
              { _id: 'eventful', name: 'Eventful' },
              { _id: 'ticketmaster', name: 'Ticketmaster' },
            ]}
            optionText="name"
            optionValue="_id"
          />
          <ReferenceInput
            label="Category"
            source="category"
            reference="categories"
            allowEmpty
          >
            <SelectInput optionText="name" optionValue="id" />
          </ReferenceInput>
          <SelectInput
            label="Status"
            source="admin_status"
            choices={[
              { _id: '', name: '' },
              { _id: 'pending', name: 'Pending' },
              { _id: 'reviewed', name: 'Reviewed' },
              { _id: 'called', name: 'Called' },
              { _id: 'online_end', name: 'Online End' },
              { _id: 'online_sched', name: 'Online Sched' },
            ]}
            optionText="name"
            optionValue="_id"
          />
          <LongTextInput source="notes" />
          {hasAlertPrivileges() && (
            <FormDataConsumer>
              {() => {
                return (
                  <div>
                    <BooleanInput
                      label="Enable Upcoming Alert"
                      source="upcoming_alert_enabled"
                    />
                    <NumberInput
                      label="Upcoming Alert Threshold (mins)"
                      source="upcoming_threshold"
                    />
                    <BooleanInput
                      label="Enable Ending Alert"
                      source="ending_alert_enabled"
                    />
                    <NumberInput
                      label="Ending Alert Threshold (mins)"
                      source="ending_threshold"
                    />
                  </div>
                );
              }}
            </FormDataConsumer>
          )}
          <DisabledInput
            source="client_timezone"
            defaultValue={clientTimezone}
          />
        </SimpleForm>
      )}
    </WithProps>
  </Create>
);

const hasAlertPrivileges = () => {
  let token = localStorage['token'];
  let parsed = Buffer(token, 'base64').toString('ascii');
  let loggedEmail = parsed.split(',').map(e => {
    return e.includes('user_email') ? e.split('"')[3] : undefined;
  })[2];

  //blacklist all console.admin accounts from having alert access
  const re = RegExp('console.admin*');

  return !re.test(loggedEmail);
};



// WEBPACK FOOTER //
// ./src/events.js